import { useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { isSchedulingOnly, isSchedulingPathAllowed, type Role } from '@/lib/mobile-mode';

// ============================================================
// MobileRouteGuard
// ============================================================
// Wraps the authenticated (non-kiosk) routes block. employee + manager on a
// PERSONAL login — the native app OR the web app on a personal device — are
// confined to the scheduling-only allow-list (see mobile-mode.ts); any path
// outside it redirects to /dashboard. corporate / admin / super_admin are
// unrestricted. The shared kiosk runs as a PIN session and is excluded both
// here (loginMethod check) and structurally (it renders under /limited,
// outside this guard).

export function MobileRouteGuard({ children }: { children: React.ReactNode }) {
  const { profile, loginMethod } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const role = (profile?.role ?? undefined) as Role;

  const restricted = isSchedulingOnly(role, loginMethod);
  const blocked = restricted && !isSchedulingPathAllowed(location.pathname, role, loginMethod);

  // Explain the bounce instead of silently teleporting the user home — tapping
  // a link or opening a bookmark used to just "do nothing", which reads as the
  // app being broken.
  useEffect(() => {
    if (!blocked) return;
    toast({
      title: 'Not available on your phone',
      description: 'The app is schedule-only here. Use the store kiosk or the web app for that.',
    });
  }, [blocked, location.pathname, toast]);

  if (!blocked) {
    return <>{children}</>;
  }
  return <Navigate to="/dashboard" replace />;
}
